import React, { useRef } from 'react';
import { ALLOWED_EXTENSIONS } from '../../constants/fileUploadConstants';

export interface FileAttachmentManagerProps {
  onFilesSelected: (files: FileList | File[]) => void;
}

/** Paperclip / attach — matches design SVG exactly (24×24 viewBox). */
const ICN_ATTACH = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

/** Shared style for the attach/mic action buttons — 30×30, rounded-[7px]. */
const ACTION_BTN_BASE = 'inline-flex shrink-0 items-center justify-center w-[30px] h-[30px] rounded-[7px] cursor-pointer transition-colors bg-transparent text-text-secondary hover:bg-surface-hover border-none';

/**
 * Renders the hidden file input and the paperclip attach button.
 * The attachment chips are rendered separately via AttachmentChipList so the
 * button only appears once, in the composer's action row.
 */
export const FileAttachmentManager: React.FC<FileAttachmentManagerProps> = ({
  onFilesSelected,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFilesSelected(e.target.files);
      // Reset input so the same file can be re-selected after removal
      e.target.value = '';
    }
  };

  const handleAttachClick = () => {
    inputRef.current?.click();
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_EXTENSIONS.join(',')}
        multiple
        style={{ display: 'none' }}
        onChange={handleInputChange}
        aria-hidden="true"
        tabIndex={-1}
      />
      <button
        type="button"
        className={ACTION_BTN_BASE}
        aria-label="Attach files"
        onClick={handleAttachClick}
        title="Attach Excel or ZIP files"
      >
        {ICN_ATTACH}
      </button>
    </>
  );
};
